<template>
  <div>
    <!-- O servidor corta a listagem; dizer isso é o que separa "rápido" de
         "incompleto sem avisar" — trilha de auditoria pela metade com cara de
         inteira é pior que a lentidão que o corte conserta. -->
    <inline-message
      v-if="truncated"
      data-testid="logs-truncado-aviso"
      severity="warn">
      {{ t('Mostrando os') }} <strong>{{ logs.length }}</strong>
      {{ t('registros mais recentes de') }} <strong>{{ total }}</strong>
      {{ t('no período. Estreite o filtro de datas para ver o restante.') }}
    </inline-message>

    <data-table
      data-testid="logs-table"
      test-id-prefix="logs"
      :config="datatableConfig"
      :data="logs"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @refresh="handleRefresh">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import DataTable from '../../grid/DataTable.vue';
import { useLogs } from '../../composables/useLogs';

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

const { t } = useI18n();

// The composable owns the network read. This screen does NOT use DataTable's
// `endpoint` loader: that loader treats `dados` as the row array, while
// Logs_BuscarLogsResponse wraps it as `{ logs: [...] }` — every load threw
// `data.map is not a function`, blanking the table. `:data` takes the
// already-unwrapped rows (same pattern as WebhookLogsTable).
const { logs, total, truncated, fetchLogs } = useLogs();

const tableColumns = computed(() => {
  return [
    {
      data: 'created_at',
      title: t('Data'),
      width: 180,
      sortable: true,
      filterType: 'text',
      renderType: 'datetime'
    },
    {
      data: 'user_name',
      title: t('Nome Usuário'),
      width: 200,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'message',
      title: t('Mensagem'),
      width: 300,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'data',
      title: t('Informações'),
      width: 300,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        const raw = params.value;
        if (raw == null || raw === '') return '';
        const value = String(raw);
        try {
          const jsonObj = JSON.parse(value);
          const jsonStr = JSON.stringify(jsonObj);
          if (jsonStr.length > 100) {
            return jsonStr.substring(0, 100) + '...';
          }
          return jsonStr;
        } catch {
          if (value.length > 100) {
            return value.substring(0, 100) + '...';
          }
          return value;
        }
      }
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `logs_${new Date().toISOString().split('T')[0]}`,
    dataKey: 'log_id',
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    rowSelection: null,
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => ({
  show: true,
  text: t('Atualizar')
}));

const csvButtonConfig = computed(() => ({
  show: true,
  text: t('Exportar CSV')
}));

const loadData = async (): Promise<void> => {
  log('LogsTable', 'loadData', 'Triggering data reload');
  try {
    await fetchLogs();
  } catch (error) {
    log('LogsTable', 'handleDataError', 'Error loading data', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar logs'));
  }
};

const handleRefresh = (): void => {
  void loadData();
};

onMounted(() => {
  log('LogsTable', 'mounted', 'Component mounted');
  void loadData();
});

defineExpose({
  loadData
});
</script>

<style scoped>
/* No additional styles needed */
</style>
